<template>
  <div class="p-4 min-h-screen bg-gray-100">
    <div class="max-w-6xl mx-auto">
      <div class="mb-3">
        <button @click="goBack" class="text-sm text-gray-600 hover:text-gray-900">← Back</button>
      </div>

      <!-- Opened from a card's Reply. The modal asks what to start from: nothing
           (write it yourself) or a template. -->
      <FollowUpTemplateModal
        :open="replyingTo !== null"
        :count="1"
        :submitting="followingUp"
        @confirm="onConfirmFollowUp"
        @cancel="replyingTo = null"
      />

      <div v-if="loading" class="text-gray-500 py-12 text-center">Loading…</div>

      <div v-else-if="!draft" class="text-gray-500 py-12 text-center">
        <template v-if="threadId">{{ notFound ? 'Conversation not found.' : 'Couldn’t load this conversation.' }}</template>
        <template v-else>{{ notFound ? 'Email not found.' : 'Couldn’t load this email.' }}</template>
      </div>

      <!-- Reading pane in the left two thirds, live company context on the right. -->
      <div v-else class="grid grid-cols-3 gap-4">
        <div class="col-span-2">
          <!-- The conversation, newest first: our sends and theirs in one list.
               Splitting the opened message out above its own thread is what made
               these read as though they began with somebody answering a message
               that wasn't shown -- every earlier send of ours was hidden. -->
          <h2 class="text-sm font-semibold text-gray-700 mb-2">
            Conversation
            <span class="font-normal text-gray-500">— {{ activitySummary }}</span>
          </h2>

          <!-- Drafts that haven't gone out, so a pending reply is found where the
               conversation is rather than only in the Drafts tab. -->
          <div v-if="threadDrafts.length" class="mb-3">
            <div class="text-xs font-semibold text-gray-600 mb-1">
              Draft{{ threadDrafts.length === 1 ? '' : 's' }} in this thread
            </div>
            <div class="space-y-2">
              <div
                v-for="d in threadDrafts"
                :key="d.id"
                class="flex items-center justify-between gap-3 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-2"
              >
                <div class="min-w-0 text-xs">
                  <div class="flex items-center gap-2">
                    <span class="text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wide bg-yellow-100 text-yellow-800 shrink-0">
                      {{ draftStatusLabel(d) }}
                    </span>
                    <span class="truncate font-medium text-gray-800">{{ d.subject || '(no subject)' }}</span>
                  </div>
                  <div class="text-gray-500 mt-0.5 truncate">
                    To {{ d.recipient_email || '—' }}<template v-if="d.created_by"> · by {{ d.created_by }}</template>
                  </div>
                </div>
                <!-- A colleague's draft on a shared thread is shown, not editable:
                     only whoever can send from its mailbox may touch it. -->
                <button
                  v-if="d.can_edit"
                  type="button"
                  @click="openInEditor(d.id)"
                  class="shrink-0 px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
                >Edit</button>
              </div>
            </div>
          </div>

          <div class="space-y-2">
            <div
              v-for="m in threadMessages"
              :key="m.id"
              class="rounded-lg border shadow-sm px-4 py-3"
              :class="kindMeta(m.kind).cardClass"
            >
              <div class="flex items-baseline justify-between gap-3">
                <div class="text-sm font-medium text-gray-800 min-w-0 flex items-baseline gap-2">
                  <span
                    class="text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wide shrink-0"
                    :class="kindMeta(m.kind).badgeClass"
                  >{{ kindMeta(m.kind).label }}</span>
                </div>
                <div class="flex items-center gap-3 shrink-0">
                  <div class="text-xs text-gray-500 whitespace-nowrap">{{ formatReceived(m.received_at) }}</div>
                  <button
                    v-if="m.can_reply"
                    type="button"
                    @click="replyingTo = { id: m.id, all: false }"
                    class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
                  >Reply</button>
                  <!-- Only when it would differ from Reply: someone else was on it. -->
                  <button
                    v-if="m.can_reply && m.reply_all_cc && m.reply_all_cc.length"
                    type="button"
                    :title="`Also cc: ${m.reply_all_cc.join(', ')}`"
                    @click="replyingTo = { id: m.id, all: true }"
                    class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
                  >Reply all</button>
                  <!-- One of ours that bounced: fix it and send it again. -->
                  <button
                    v-if="m.can_edit"
                    type="button"
                    @click="openInEditor(m.id)"
                    class="px-2 py-0.5 rounded-md text-xs border border-red-300 bg-white text-red-700 hover:bg-red-50"
                  >Edit &amp; resend</button>
                  <button
                    v-if="m.can_open_in_outlook"
                    type="button"
                    @click="onOpenInOutlook(m.id)"
                    class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
                  >Open in Outlook</button>
                </div>
              </div>

              <!-- From and To spelled out on every message. A thread spans more
                   than one mailbox and more than one contact -- a colleague
                   looped in, a send from a delegated mailbox -- so "who wrote to
                   whom" can change between one message and the next. -->
              <dl class="mt-1 text-xs leading-5">
                <div class="flex gap-2">
                  <dt class="text-gray-400 w-10 shrink-0">From:</dt>
                  <dd class="text-gray-700 truncate">
                    <span v-if="m.from_name" class="font-medium">{{ m.from_name }}</span>
                    <span :class="m.from_name ? 'text-gray-500' : 'font-medium'">
                      {{ m.from_name ? `<${m.from_email}>` : (m.from_email || 'Unknown sender') }}
                    </span>
                    <!-- Written by someone other than the mailbox's owner. -->
                    <base-pill-popover-hover
                      v-if="m.sent_by"
                      class="ml-1 border-none bg-indigo-100 text-indigo-700 text-[10px] font-semibold py-0.5 align-middle"
                      popoverTitle="Sent by"
                      :popoverBody="m.sent_by.name"
                    >Sent by {{ m.sent_by.initials }}</base-pill-popover-hover>
                  </dd>
                </div>
                <div v-if="m.to_email" class="flex gap-2">
                  <dt class="text-gray-400 w-10 shrink-0">To:</dt>
                  <dd class="text-gray-700 truncate">{{ m.to_email }}</dd>
                </div>
              </dl>

              <p v-if="m.status === 'failed'" class="text-xs text-red-600 mt-1">
                Didn’t deliver — this never reached the recipient.
              </p>
              <div v-if="m.subject" class="text-xs text-gray-500 mt-1 truncate font-medium">{{ m.subject }}</div>
              <!-- Sanitized server-side (nh3: scripts stripped, remote images
                   dropped). An inbound message carries its new content only,
                   quoted history removed; a bounce carries the mail system's
                   report. -->
              <div
                v-if="m.body_html"
                class="text-sm text-gray-700 mt-2 email-body"
                style="color: #000000"
                v-html="renderEmailBody(m.body_html, m.inline_images)"
              ></div>

              <!-- Something the sweep recorded (a reply, an Outlook send): only
                   the fact that it carried files is kept, not the files. -->
              <div v-if="m.has_attachments" class="mt-3 flex items-center gap-1 text-xs text-gray-500">
                <PaperClipIcon class="w-4 h-4 text-gray-400" />
                Has attachments, open in Outlook to view
              </div>

              <!-- The files on one of our app sends, each on the message that
                   carried them. -->
              <div v-if="m.attachments && m.attachments.length" class="mt-3">
                <div class="text-xs text-gray-500 mb-2">
                  {{ m.attachments.length }} attachment{{ m.attachments.length === 1 ? '' : 's' }}
                </div>
                <div class="flex flex-wrap gap-2">
                  <a
                    v-for="attachment in m.attachments"
                    :key="attachment.key || attachment.id"
                    :href="attachment.url"
                    target="_blank"
                    rel="noopener"
                    class="flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 hover:bg-gray-100 text-sm max-w-[260px]"
                  >
                    <PaperClipIcon class="w-4 h-4 text-gray-400 shrink-0" />
                    <span class="truncate text-gray-700">{{ attachment.filename }}</span>
                    <span class="text-xs text-gray-400 whitespace-nowrap">{{ formatSize(attachment.size) }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <p class="text-xs text-gray-400 mt-3 text-center">
            Rendered preview. Exact appearance varies by the recipient's mail client.
          </p>
        </div>

        <!-- Live company context. Campaign / next action are the company's CURRENT
             values, not the copy queued onto the draft when it was sent. -->
        <aside class="col-span-1 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm h-fit">
          <h2 class="font-semibold text-gray-700 mb-2">Company</h2>
          <dl class="space-y-3">
            <div>
              <dt class="text-xs text-gray-400">Name</dt>
              <dd class="break-words">{{ panel.company_name || '—' }}</dd>
            </div>
            <div v-if="panel.dba">
              <dt class="text-xs text-gray-400">DBA</dt>
              <dd class="break-words">{{ panel.dba }}</dd>
            </div>
            <div>
              <dt class="text-xs text-gray-400">Current campaign</dt>
              <dd class="break-words">{{ panel.campaign ? panel.campaign.campaign_name : '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs text-gray-400">Next action</dt>
              <dd class="whitespace-pre-wrap text-gray-700">{{ panel.crm_next_action || '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs text-gray-400">Next action due</dt>
              <dd>{{ nextActionDue }}</dd>
            </div>
            <div class="border-t border-gray-200 pt-2">
              <button type="button" @click="showResearch = !showResearch" class="flex items-center gap-1 w-full text-left">
                <ChevronDownIcon v-if="showResearch" class="w-4 h-4 text-gray-400" />
                <ChevronRightIcon v-else class="w-4 h-4 text-gray-400" />
                <span class="text-xs text-gray-400">Research notes</span>
              </button>
              <dd v-if="showResearch" class="whitespace-pre-wrap text-gray-700 mt-1">{{ panel.account_manager_research_notes || '—' }}</dd>
            </div>
          </dl>
          <button
            type="button"
            :disabled="!companyId || loadingCRMDetails"
            @click="openCRMDetails"
            class="mt-4 w-full px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-wait"
          >{{ loadingCRMDetails ? 'Loading…' : 'Open CRM details' }}</button>
        </aside>
      </div>

      <base-modal :is-form-visible="showCRMDetails" @close="closeCRMDetails">
        <template #header>
          <h2 class="text-lg">{{ panel.company_name }} CRM Details</h2>
        </template>
        <template #body>
          <CRMDetailsPage
            v-if="crmDetailsCompany"
            :company="crmDetailsCompany"
            :campaigns="crmDetailsCampaigns"
            :crm-statuses="crmStatuses"
            :crm-action-types="crmActionTypes"
            @refetch-companies="reloadCRMDetailsCompany"
          />
        </template>
      </base-modal>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { PaperClipIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
import { getOutreachDraft, prepareFollowUpOutreachDraft, replyToOutreachDraft } from '@/services/OutreachDraft'
import { getOutreachThread, prepareFollowUpInOutreachThread, replyInOutreachThread } from '@/services/OutreachThread'
import { getCompanyByID } from '@/services/Company'
import { getCampaigns } from '@/services/Campaign'
import { getCrmStatuses } from '@/services/CrmStatus'
import { getActions } from '@/services/CrmAction'
import FollowUpTemplateModal from '@/components/outreach-drafts/FollowUpTemplateModal.vue'
import CRMDetailsPage from '@/components/CRMDetailsPage.vue'
import { renderEmailBody } from '@/utils/emailHtml'
import { kindMeta, summarizeKinds } from '@/utils/outreachResponseKind'
import { openInOutlook } from '@/utils/openInOutlook'

const store = useStore()
const route = useRoute()
const router = useRouter()
const $toast = useToast()

// What this page shows: a thread, opened by its own id (/outreach-threads/:threadId),
// or the thread one of our emails is on (/outreach-drafts/:id/preview). A thread
// imported from Outlook has no email of ours, so it can only open the first way.
// Either way the response carries thread_messages, thread_drafts and
// reference_panel, which is all the page renders.
const threadId = computed(() => route.params.threadId || null)
const draft = ref(null)
const loading = ref(false)
// A 404 is "not found"; anything else is a failure to load, and saying the
// email doesn't exist when the server errored would send someone looking for
// a deleted message that's still there.
const notFound = ref(false)
const followingUp = ref(false)
// Id of the thread message whose Reply opened the modal; null when closed.
const replyingTo = ref(null)

async function onOpenInOutlook(messageId) {
  const error = await openInOutlook(messageId, store.state.auth.token)
  if (error) $toast.error(error)
}

// The whole conversation, newest first (server order), our sends included.
const threadMessages = computed(() => (draft.value && draft.value.thread_messages) || [])
// Unsent drafts on the same thread, newest first.
const threadDrafts = computed(() => (draft.value && draft.value.thread_drafts) || [])

function draftStatusLabel(d) {
  if (d.status === 'scheduled') return 'Scheduled'
  if (d.status === 'failed') return 'Failed to send'
  return 'Draft'
}

// Into the editor, carrying this page's own address so the editor's Back
// returns to the thread rather than the list.
function openInEditor(id) {
  router.push({ path: `/outreach-drafts/${id}`, query: { ...route.query, from: route.fullPath } })
}
// "2 sent · 1 reply" -- only the kinds actually on this thread.
const activitySummary = computed(() => summarizeKinds(threadMessages.value))

// Live company data the server reads off the company at request time, so the
// panel reflects where the company stands now rather than at time of send.
const panel = computed(() => (draft.value && draft.value.reference_panel) || {})
// A thread carries company_id (its `company` is {id, name}); an email carries
// the id as `company`.
const companyId = computed(() => (draft.value && (draft.value.company_id || draft.value.company))
  || panel.value.company_id || null)
const showResearch = ref(false)

const nextActionDue = computed(() => {
  const due = panel.value.crm_next_action_due_date
  if (!due) return '—'
  // Date-only value: parse the parts directly so it doesn't shift a day in
  // timezones behind UTC the way new Date('YYYY-MM-DD') would.
  const parts = String(due).split('-')
  if (parts.length !== 3) return due
  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString()
})

const showCRMDetails = ref(false)
const loadingCRMDetails = ref(false)
const crmDetailsCompany = ref(null)
const crmDetailsCampaigns = ref([])
const crmStatuses = ref([])
const crmActionTypes = ref([])

// getCompanyByID resolves with the axios error on failure rather than throwing,
// so check for a real company before handing it to CRMDetailsPage.
async function fetchCompanyDetail() {
  const result = await getCompanyByID({ token: store.state.auth.token, companyId: companyId.value })
  const company = result && result.response && result.response.data
  if (!company || !company.id) throw new Error('Company not found')
  return company
}

// CRMDetailsPage needs the full company plus the campaign / status / detail-type
// option lists. Fetched on demand so the preview itself stays a single request.
async function openCRMDetails() {
  if (!companyId.value || loadingCRMDetails.value) return
  loadingCRMDetails.value = true
  try {
    const token = store.state.auth.token
    const [company, campaigns, statuses, actionTypes] = await Promise.all([
      fetchCompanyDetail(),
      getCampaigns(token),
      getCrmStatuses(token),
      getActions(token),
    ])
    crmDetailsCompany.value = company
    crmDetailsCampaigns.value = campaigns
    crmStatuses.value = statuses
    crmActionTypes.value = actionTypes.data
    showCRMDetails.value = true
  } catch (e) {
    $toast.error('Could not load CRM details')
  } finally {
    loadingCRMDetails.value = false
  }
}

// Edits in the modal change the company's next action / campaign, so pull the
// draft again too and the panel behind the modal stays in step. Deliberately not
// via load() -- flipping `loading` would tear down the open modal.
async function reloadCRMDetailsCompany() {
  try {
    crmDetailsCompany.value = await fetchCompanyDetail()
    draft.value = await fetchView()
  } catch (e) {
    // Keep the modal open on the values it already has rather than blanking it.
    $toast.error('Could not refresh company')
  }
}

function closeCRMDetails() {
  showCRMDetails.value = false
}

function formatReceived(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString() } catch (e) { return iso }
}


function formatSize(bytes) {
  if (bytes === null || bytes === undefined) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Back to wherever this preview was opened from. ?from= carries a full in-app
// path (the Outreach Responses feed uses it to preserve its filters); otherwise
// it's the drafts queue, on the tab the ?tab= param names. Only same-origin
// relative paths are honored, so a hand-edited ?from= can't send a user off-site.
function goBack() {
  const from = route.query.from
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) {
    router.push(from)
    return
  }
  // Everything but `from` (this page's own return-path param) goes back, so the
  // queue's tab and filters are restored.
  const query = { ...route.query }
  delete query.from
  router.push({ path: '/outreach-drafts', query })
}

// Create a reply to the card that was clicked and open it for editing. `choice`
// is 'bespoke' (no template, empty body), null (reuse a template) or a template
// id. Opened as a thread, the thread is the access gate; opened as an email, that
// email's id is. Either way the clicked message rides along as message_id.
function createReply(choice, messageId, replyAll) {
  const token = store.state.auth.token
  if (threadId.value) {
    return choice === 'bespoke'
      ? replyInOutreachThread(threadId.value, messageId, token, replyAll)
      : prepareFollowUpInOutreachThread(threadId.value, messageId, token, choice, replyAll)
  }
  return choice === 'bespoke'
    ? replyToOutreachDraft(draft.value.id, token, messageId, replyAll)
    : prepareFollowUpOutreachDraft(draft.value.id, token, choice, messageId, replyAll)
}

async function onConfirmFollowUp(choice) {
  followingUp.value = true
  const { id: messageId, all: replyAll } = replyingTo.value
  try {
    const created = await createReply(choice, messageId, replyAll)
    $toast.success('Reply draft created')
    router.push(`/outreach-drafts/${created.id}`)
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not create a reply')
  } finally {
    followingUp.value = false
  }
}

function fetchView() {
  const token = store.state.auth.token
  return threadId.value
    ? getOutreachThread(threadId.value, token)
    : getOutreachDraft(route.params.id, token)
}

async function load() {
  loading.value = true
  notFound.value = false
  try {
    draft.value = await fetchView()
  } catch (e) {
    notFound.value = e?.response?.status === 404
    $toast.error(threadId.value ? 'Failed to load conversation' : 'Failed to load email')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  // The router leaves scroll alone outside the installed PWA, so without this
  // the page opens at whatever offset the list it came from was scrolled to.
  window.scrollTo(0, 0)
  load()
})
</script>

<style scoped>
/* Give the rendered email body sensible defaults so template HTML reads like an
   email rather than inheriting the app's resets. */
.email-body :deep(img) {
  max-width: 100%;
  height: auto;
}
.email-body :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}
.email-body :deep(p) {
  margin: 0 0 0.5rem;
}
/* Bounce bodies are the mail system's report, escaped into a <pre> at ingest.
   Wrap it, or a long diagnostic line stretches the card off the page. */
.email-body :deep(pre) {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.75rem;
  line-height: 1.15rem;
  margin: 0;
}
</style>
